import { useMemo, type JSX } from 'react'
import { Table } from '@/components/Table'
import { useRegistry } from '@/hooks/useRegistry'
import { useSections } from '@/hooks/useSections'
import { LoadingText } from '@/components/LoadingText'
import { Toolbar } from './Toolbar'
import { useOrganisers } from '@/hooks/useOrganisers'
import { useAuthors } from '@/hooks/useAuthors'
import type { Event, EventFields } from '@/shared/schemas/event'
import type { Planning, PlanningFields } from '@/shared/schemas/planning'
import type { Article } from '@/shared/schemas/article'
import { useDocuments } from '@/hooks/index/useDocuments'
import { createSearchColumns } from './lib/createSearchColumns'
import type { SearchKeys } from '@/hooks/index/useDocuments/queries/views/search'
import search from '@/hooks/index/useDocuments/queries/views/search'
import { useQuery } from '@/hooks/useQuery'
import type { ColumnDef } from '@tanstack/react-table'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Pagination } from '@/components/Table/Pagination'

const PAGE_SIZE = 100

export const SearchResult = ({ searchType, page }: {
  searchType: SearchKeys
  page: number
}): JSX.Element => {
  const sections = useSections()
  const organisers = useOrganisers()
  const authors = useAuthors()
  const [filter] = useQuery()
  const { t } = useTranslation()
  const { locale, timeZone } = useRegistry()

  const getType = (searchType: SearchKeys) => searchType === 'events' ? 'Event' : searchType === 'articles' ? 'Editor' : 'Planning'

  const searchParams = search[searchType].params(filter, timeZone)

  const { error, isLoading, total } = useDocuments<Planning | Event | Article, PlanningFields | EventFields>({
    ...searchParams,
    page,
    size: PAGE_SIZE,
    options: {
      setTableData: true
    }
  })
  const columns = useMemo(() => {
    return createSearchColumns({
      searchType,
      sections,
      authors,
      locale,
      timeZone,
      organisers
    }, t)
  }, [locale, timeZone, authors, sections, searchType, organisers, t])

  if (error) {
    console.error('Error fetching search result items:', error)
    toast.error(t('errors:toasts.getSearchResultsFailed'))
  }

  return (
    <>
      {isLoading
        ? (
            <LoadingText>{t('common:misc.loading')}</LoadingText>
          )
        : (
            <>
              <Toolbar type={searchType} />
              <Table
                type='Search'
                searchType={getType(searchType)}
                columns={columns as ColumnDef<Planning | Event>[]}
              />
            </>
          )}
      <div className='flex justify-center w-full'>
        <Pagination total={total} pageSize={PAGE_SIZE} />
      </div>
    </>
  )
}
